// src/app/providers/BrandProvider.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useConfig } from "./ConfigProvider";
import { proxyPut } from "@/api/proxy.js";
import { CV_FIELD_KEYS } from "@/shared/configKeys.js";

const BrandContext = createContext(null);

const STORAGE_KEY = "YG_THEME_MODE";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse a hex color (#rrggbb or #rgb) into { h, s, l } (0-360, 0-100, 0-100).
 */
function hexToHSL(hex) {
  let r = 0, g = 0, b = 0;
  const h6 = hex.replace("#", "");
  if (h6.length === 3) {
    r = parseInt(h6[0] + h6[0], 16);
    g = parseInt(h6[1] + h6[1], 16);
    b = parseInt(h6[2] + h6[2], 16);
  } else {
    r = parseInt(h6.slice(0, 2), 16);
    g = parseInt(h6.slice(2, 4), 16);
    b = parseInt(h6.slice(4, 6), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Generate a 50-900 Chakra-style palette from a single hex color.
 * 500 = the input color, lighter above, darker below.
 */
function generatePalette(hex) {
  const { h, s } = hexToHSL(hex);
  // Target lightness for each stop (50 = very light, 900 = very dark)
  const stops = {
    50:  95,
    100: 89,
    200: 79,
    300: 67,
    400: 55,
    500: null, // use original
    600: 38,
    700: 30,
    800: 23,
    900: 16,
  };
  const palette = {};
  for (const [key, targetL] of Object.entries(stops)) {
    if (targetL === null) {
      palette[key] = hex;
    } else {
      palette[key] = hslToHex(h, Math.min(s, 90), targetL);
    }
  }
  return palette;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function BrandProvider({ children }) {
  const { config, setConfig } = useConfig();

  // Initialize from server config first, localStorage as fallback
  const [themeMode, setThemeMode] = useState(() => {
    const fromConfig = config.brand_theme_mode;
    if (fromConfig && fromConfig !== "default") return fromConfig;
    return localStorage.getItem(STORAGE_KEY) || "default";
  });

  // Sync from server when config loads (config.brand_theme_mode may arrive async)
  const configThemeMode = config.brand_theme_mode;
  useEffect(() => {
    if (configThemeMode && configThemeMode !== "default") {
      setThemeMode(configThemeMode);
    }
  }, [configThemeMode]);

  const brandConfig = useMemo(() => ({
    logoUrl: config.brand_logo_url || "",
    primaryColor: config.brand_primary_color || "",
    accentColor: config.brand_accent_color || "",
    fontHeading: config.brand_font_heading || "",
    fontBody: config.brand_font_body || "",
  }), [
    config.brand_logo_url,
    config.brand_primary_color,
    config.brand_accent_color,
    config.brand_font_heading,
    config.brand_font_body,
  ]);

  const hasBrand = !!(brandConfig.logoUrl || brandConfig.primaryColor);

  // Persist theme mode to localStorage + server
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, themeMode);
    // Also persist to config
    if (config.brand_theme_mode !== themeMode) {
      setConfig((prev) => ({ ...prev, brand_theme_mode: themeMode }));
    }
  }, [themeMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply CSS custom properties when in brand mode
  useEffect(() => {
    const root = document.documentElement;
    if (themeMode === "brand" && brandConfig.primaryColor) {
      const palette = generatePalette(brandConfig.primaryColor);
      root.style.setProperty("--brand-primary", brandConfig.primaryColor);
      root.style.setProperty("--brand-accent", brandConfig.accentColor || brandConfig.primaryColor);
      // Expose palette shades as CSS vars for Chakra overrides
      for (const [stop, color] of Object.entries(palette)) {
        root.style.setProperty(`--brand-${stop}`, color);
      }
      if (brandConfig.fontHeading) root.style.setProperty("--brand-font-heading", brandConfig.fontHeading);
      if (brandConfig.fontBody) root.style.setProperty("--brand-font-body", brandConfig.fontBody);
      root.classList.add("brand-theme");
    } else {
      root.classList.remove("brand-theme");
      root.style.removeProperty("--brand-primary");
      root.style.removeProperty("--brand-accent");
      for (const stop of [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]) {
        root.style.removeProperty(`--brand-${stop}`);
      }
      root.style.removeProperty("--brand-font-heading");
      root.style.removeProperty("--brand-font-body");
    }
  }, [themeMode, brandConfig]);

  const toggleTheme = useCallback(() => {
    setThemeMode((m) => (m === "default" ? "brand" : "default"));
  }, []);

  const value = useMemo(
    () => ({ themeMode, setThemeMode, toggleTheme, brandConfig, hasBrand }),
    [themeMode, setThemeMode, toggleTheme, brandConfig, hasBrand]
  );

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
}

/** Hook */
export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error("useBrand must be used within BrandProvider");
  return ctx;
}

export { generatePalette };
